import * as React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FaBolt, FaPlug, FaCheckCircle } from 'react-icons/fa';

const NAVY = '#1a365d';
const SPARK = '#f6e05e';
const AMBER = '#ed8936';
const LIVE = '#48bb78';
const DIM = '#cbd5e0';
const METER_OFF = '#2d3748';

/** How long the pick-ticket PICKED stamp shows before the API call / overlay starts. */
export const CED_PICKED_STAMP_MS = 1800;
/** Minimum time the processing animation stays visible once the API call begins. */
export const CED_MIN_PROCESSING_MS = 2800;

export const DEFAULT_CED_CATALOG_URL = 'https://www.catalogs.grafcow.com/ced-catalog';

const loadingPhrases = [
  'Connecting the circuit…',
  'Energizing your CED order…',
  'Routing power to fulfillment…',
  'Almost live…',
];

/** Crackling bolts + pulsing plug while Place Order hits the API (viewport-centered overlay). */
export const CedOrderProcessing: React.FC = () => {
  const [phraseIndex, setPhraseIndex] = React.useState(0);

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setPhraseIndex((i) => (i + 1) % loadingPhrases.length);
    }, 1600);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div style={processingOverlayStyle} role="status" aria-live="polite" aria-busy="true">
      <motion.div
        style={processingCardStyle}
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 280, damping: 20 }}
      >
        <div style={processingHeadline}>Placing your order…</div>
        <div style={boltStage}>
          {[ -48, 0, 48 ].map((x, i) => (
            <motion.div
              key={i}
              style={{ ...boltFloat, left: `calc(50% + ${x}px)` }}
              animate={{
                opacity: [0.2, 1, 0.35, 1, 0.2],
                scale: [0.85, 1.2, 0.9, 1.28, 0.85],
                y: [0, -8, 2, -10, 0],
                rotate: [-8, 8, -4, 12, -8],
              }}
              transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.18, ease: 'easeInOut' }}
            >
              <FaBolt color={i === 1 ? SPARK : AMBER} size={i === 1 ? 36 : 26} />
            </motion.div>
          ))}
          <motion.div
            style={plugWrap}
            animate={{ scale: [1, 1.08, 1], filter: ['brightness(1)', 'brightness(1.4)', 'brightness(1)'] }}
            transition={{ duration: 1.1, repeat: Infinity }}
          >
            <FaPlug color={NAVY} size={28} />
          </motion.div>
        </div>

        <motion.div
          style={currentBar}
          animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={phraseIndex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            style={phraseStyle}
          >
            {loadingPhrases[phraseIndex]}
          </motion.div>
        </AnimatePresence>
        <div style={subPhrase}>Consolidated Electrical Distributors</div>
      </motion.div>
    </div>
  );
};

interface CedConfirmProps {
  onClose: () => void;
  catalogUrl?: string;
}

/** Full-screen “power on” celebration after a successful CED order. */
export const CedOrderConfirmation: React.FC<CedConfirmProps> = ({ onClose, catalogUrl = DEFAULT_CED_CATALOG_URL }) => {
  const sparks = React.useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 280,
        y: -40 - Math.random() * 160,
        delay: Math.random() * 0.45,
        rot: Math.random() * 60 - 30,
        size: 10 + Math.random() * 16,
      })),
    []
  );

  return (
    <div style={popupBackgroundStyle}>
      <motion.div
        style={popupStyle}
        initial={{ scale: 0.7, opacity: 0, rotate: -2 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18 }}
      >
        <div style={confirmStage}>
          {sparks.map((s) => (
            <motion.div
              key={s.id}
              style={{ position: 'absolute', left: '50%', top: '55%', pointerEvents: 'none' }}
              initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
              animate={{
                x: s.x,
                y: s.y,
                opacity: [0, 1, 0],
                scale: [0.4, 1.2, 0.6],
                rotate: s.rot,
              }}
              transition={{ duration: 1.1, delay: 0.35 + s.delay, ease: 'easeOut' }}
            >
              <FaBolt color={s.id % 2 === 0 ? SPARK : AMBER} size={s.size} />
            </motion.div>
          ))}

          <motion.div
            initial={{ scale: 0, rotate: -40, opacity: 0 }}
            animate={{ scale: [0, 1.4, 1], rotate: [-40, 10, 0], opacity: 1 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
            style={heroIcon}
          >
            <FaBolt color={SPARK} size={64} style={{ filter: 'drop-shadow(0 0 12px rgba(246,224,94,0.9))' }} />
          </motion.div>

          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.55, type: 'spring', stiffness: 300, damping: 16 }}
            style={checkBadge}
          >
            <FaCheckCircle color={LIVE} size={28} />
          </motion.div>
        </div>

        <motion.h2
          style={confirmTitle}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          Order connected!
        </motion.h2>
        <motion.p
          style={confirmBody}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
        >
          Your CED apparel order is in. Check your email for confirmation. We&apos;ll send a QuickBooks invoice when your order ships so you can pay online.
        </motion.p>

        <motion.div
          style={confirmCatalogCardStyle}
          role="region"
          aria-label="CED apparel catalog"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.72 }}
        >
          <div style={confirmCatalogAccentStyle} aria-hidden="true" />
          <div style={confirmCatalogIconWrapStyle} aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 5.5C4 4.67 4.67 4 5.5 4H18.5C19.33 4 20 4.67 20 5.5V18.5C20 19.33 19.33 20 18.5 20H5.5C4.67 20 4 19.33 4 18.5V5.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <path d="M8 4V20M16 4V20" stroke="currentColor" strokeWidth="1.5" />
              <path d="M4 9H20M4 15H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <div style={confirmCatalogTextWrapStyle}>
            <p style={confirmCatalogHeadingStyle}>Looking for more?</p>
            <a
              href={catalogUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={confirmCatalogLinkStyle}
            >
              Custom CED guide ↗
            </a>
          </div>
        </motion.div>

        <motion.button
          type="button"
          style={closeButtonStyle}
          onClick={onClose}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Close
        </motion.button>
      </motion.div>
    </div>
  );
};

const processingOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(15, 23, 42, 0.72)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  padding: '16px',
  boxSizing: 'border-box',
};

const processingCardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: 360,
  padding: '28px 20px 22px',
  boxSizing: 'border-box',
  overflow: 'visible',
  borderRadius: 14,
  border: `2px solid ${NAVY}`,
  borderTop: `5px solid ${SPARK}`,
  background: 'linear-gradient(180deg, #fff 0%, #f7fafc 100%)',
  boxShadow: '0 16px 40px rgba(26, 54, 93, 0.35)',
};

const processingHeadline: React.CSSProperties = {
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 17,
  fontWeight: 700,
  color: NAVY,
  textAlign: 'center',
  marginBottom: 12,
  letterSpacing: '0.02em',
};

const boltStage: React.CSSProperties = {
  position: 'relative',
  width: 180,
  height: 78,
  marginBottom: 14,
  overflow: 'visible',
};

const boltFloat: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  marginLeft: -12,
};

const plugWrap: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: 28,
  marginLeft: -14,
};

const currentBar: React.CSSProperties = {
  width: '88%',
  maxWidth: 280,
  height: 8,
  borderRadius: 999,
  backgroundImage: `linear-gradient(90deg, ${NAVY} 0%, ${AMBER} 35%, ${SPARK} 50%, ${AMBER} 65%, ${NAVY} 100%)`,
  backgroundSize: '200% 100%',
  boxShadow: `0 0 12px rgba(237, 137, 54, 0.55)`,
  marginBottom: 10,
};

const phraseStyle: React.CSSProperties = {
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 15,
  fontWeight: 700,
  color: NAVY,
  textAlign: 'center',
};

const subPhrase: React.CSSProperties = {
  marginTop: 4,
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#718096',
};

const popupBackgroundStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(15, 23, 42, 0.72)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const popupStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  padding: '28px 28px 24px',
  borderRadius: 14,
  boxShadow: '0 16px 40px rgba(26, 54, 93, 0.35)',
  textAlign: 'center',
  maxWidth: 480,
  width: '92%',
  position: 'relative',
  overflow: 'hidden',
  borderTop: `5px solid ${SPARK}`,
};

const confirmStage: React.CSSProperties = {
  position: 'relative',
  height: 120,
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const heroIcon: React.CSSProperties = {
  position: 'relative',
  zIndex: 2,
};

const checkBadge: React.CSSProperties = {
  position: 'absolute',
  right: 'calc(50% - 52px)',
  bottom: 18,
  zIndex: 3,
  background: '#fff',
  borderRadius: '50%',
  lineHeight: 0,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
};

const confirmTitle: React.CSSProperties = {
  margin: '0 0 10px',
  fontFamily: "Georgia, 'Times New Roman', serif",
  fontSize: 28,
  color: NAVY,
  fontWeight: 'normal' as any,
};

const confirmBody: React.CSSProperties = {
  margin: '0 0 18px',
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 15,
  lineHeight: 1.55,
  color: '#4a5568',
};

const confirmCatalogCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '0 0 20px',
  padding: '14px 16px',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)',
  border: '1px solid #e2e8f0',
  boxShadow: '0 4px 12px rgba(26, 54, 93, 0.08)',
  textAlign: 'left',
  position: 'relative',
  overflow: 'hidden',
};

const confirmCatalogAccentStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 4,
  background: `linear-gradient(180deg, ${NAVY} 0%, #3182ce 100%)`,
};

const confirmCatalogIconWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 40,
  height: 40,
  marginLeft: 4,
  borderRadius: 10,
  backgroundColor: 'rgba(26, 54, 93, 0.08)',
  color: NAVY,
};

const confirmCatalogTextWrapStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const confirmCatalogHeadingStyle: React.CSSProperties = {
  margin: '0 0 6px',
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 15,
  fontWeight: 600,
  color: '#1a202c',
};

const confirmCatalogLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 16px',
  borderRadius: 999,
  background: `linear-gradient(135deg, ${NAVY} 0%, #2c5282 100%)`,
  color: '#ffffff',
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 15,
  fontWeight: 600,
  textDecoration: 'none',
  boxShadow: '0 3px 10px rgba(26, 54, 93, 0.22)',
};

const closeButtonStyle: React.CSSProperties = {
  padding: '12px 28px',
  backgroundColor: NAVY,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

/* ─── Field power-meter (name → email → address) ─── */

export interface CedFieldPowerMeterProps {
  nameReady: boolean;
  emailReady: boolean;
  locationReady: boolean;
  addressReady: boolean;
}

const meterSteps: { key: keyof CedFieldPowerMeterProps; label: string }[] = [
  { key: 'nameReady', label: 'Name' },
  { key: 'emailReady', label: 'Email' },
  { key: 'locationReady', label: 'Location' },
  { key: 'addressReady', label: 'Address' },
];

/** Compact four-segment progress as required CED fields fill. */
export const CedFieldPowerMeter: React.FC<CedFieldPowerMeterProps> = ({
  nameReady,
  emailReady,
  locationReady,
  addressReady,
}) => {
  const reduceMotion = useReducedMotion();
  const flags = { nameReady, emailReady, locationReady, addressReady };
  const lit = meterSteps.filter((s) => flags[s.key]).length;
  const totalSteps = meterSteps.length;

  return (
    <div style={meterWrap} aria-label={`Checkout progress: ${lit} of ${totalSteps} required sections`}>
      <div style={meterCaption}>
        <FaBolt size={10} color={lit > 0 ? SPARK : DIM} style={{ marginRight: 6, opacity: 0.85 }} />
        Field power
        <span style={meterCount}>{lit}/{totalSteps}</span>
      </div>
      <div style={meterTrack}>
        {meterSteps.map((step, i) => {
          const on = flags[step.key];
          return (
            <div key={step.key} style={meterSegCol}>
              <motion.div
                style={meterSeg}
                initial={false}
                animate={{
                  backgroundColor: on ? (i === totalSteps - 1 ? LIVE : AMBER) : METER_OFF,
                  boxShadow: on && !reduceMotion
                    ? `0 0 6px ${i === totalSteps - 1 ? 'rgba(72,187,120,0.35)' : 'rgba(237,137,54,0.3)'}`
                    : '0 0 0 transparent',
                }}
                transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: 'easeOut' }}
              />
              <span style={{ ...meterLabel, color: on ? '#e2e8f0' : '#718096' }}>{step.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const meterWrap: React.CSSProperties = {
  width: '90%',
  maxWidth: 420,
  margin: '0 auto 18px',
  padding: '10px 12px 8px',
  borderRadius: 8,
  background: 'rgba(0,0,0,0.22)',
  border: '1px solid rgba(255,255,255,0.08)',
};

const meterCaption: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 11,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#a0aec0',
  marginBottom: 8,
};

const meterCount: React.CSSProperties = {
  marginLeft: 'auto',
  fontVariantNumeric: 'tabular-nums',
  color: '#e2e8f0',
  letterSpacing: '0.04em',
};

const meterTrack: React.CSSProperties = {
  display: 'flex',
  gap: 8,
};

const meterSegCol: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
};

const meterSeg: React.CSSProperties = {
  width: '100%',
  height: 6,
  borderRadius: 3,
  backgroundColor: METER_OFF,
};

const meterLabel: React.CSSProperties = {
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  fontSize: 10,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

/* ─── Breaker-style size cards ─── */

export interface CedBreakerSizeCardProps {
  size: string;
  value: number;
  onChange: (evt: React.FormEvent<HTMLInputElement>) => void;
  disabled: boolean;
  style?: React.CSSProperties;
  displayLabel: string;
}

function cedSurchargeLabel(size: string): string | null {
  if (size === 'XXL' || size === 'XXXL') return '+$2 extra';
  if (size === 'XXXXL' || size === 'XXXXXL') return '+$3 extra';
  return null;
}

/** Size card with a subtle “breaker engaged” toggle when qty goes 0 → 1+. */
export const CedBreakerSizeCard: React.FC<CedBreakerSizeCardProps> = ({
  size,
  value,
  onChange,
  disabled,
  style,
  displayLabel,
}) => {
  const reduceMotion = useReducedMotion();
  const engaged = value > 0;
  const surcharge = cedSurchargeLabel(size);

  return (
    <motion.div
      id="sizeCard"
      style={{
        ...breakerCardBase,
        ...style,
        borderColor: engaged ? NAVY : '#e0e0e0',
        backgroundColor: engaged ? '#f7fafc' : '#fff',
      }}
      initial={false}
      animate={
        reduceMotion
          ? {}
          : {
              scale: engaged ? 1 : 0.985,
              y: engaged ? 0 : 1,
            }
      }
      transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 28 }}
    >
      <div
        style={{
          ...breakerRail,
          backgroundColor: engaged ? AMBER : '#e2e8f0',
        }}
      />
      <div style={breakerBody}>
        <div style={breakerLabelRow}>
          <span style={{ ...breakerSizeLabel, color: engaged ? NAVY : '#333' }}>{displayLabel}</span>
          <span
            style={{
              ...breakerBadge,
              backgroundColor: engaged ? NAVY : '#edf2f7',
              color: engaged ? '#fff' : '#a0aec0',
            }}
            aria-hidden
          >
            {engaged ? 'ON' : 'OFF'}
          </span>
        </div>
        {surcharge && (
          <div style={{ ...breakerSurcharge, color: '#D47C61' }}>
            {surcharge}
          </div>
        )}
        <input
          type="number"
          name={size}
          value={value.toString()}
          onChange={onChange}
          disabled={disabled}
          min={0}
          style={{
            ...breakerInput,
            borderColor: engaged ? NAVY : '#ccc',
          }}
        />
      </div>
    </motion.div>
  );
};

const breakerCardBase: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  padding: 0,
  margin: '10px 0',
  border: '1.5px solid #e0e0e0',
  borderRadius: 8,
  boxShadow: '0 2px 5px rgba(26,54,93,0.08)',
  backgroundColor: '#fff',
  overflow: 'hidden',
  minHeight: 80,
  width: '100%',
  boxSizing: 'border-box',
};

const breakerRail: React.CSSProperties = {
  width: 5,
  flexShrink: 0,
  transition: 'background-color 0.25s ease',
};

const breakerBody: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 10px',
  gap: 2,
};

const breakerLabelRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
};

const breakerSizeLabel: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: 15,
  fontFamily: 'CenturyGothic, Arial, sans-serif',
};

const breakerBadge: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.06em',
  padding: '2px 5px',
  borderRadius: 3,
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  lineHeight: 1.2,
};

const breakerSurcharge: React.CSSProperties = {
  textAlign: 'center',
  fontSize: 10,
  marginTop: 1,
  lineHeight: 1.25,
  fontFamily: 'CenturyGothic, Arial, sans-serif',
};

const breakerInput: React.CSSProperties = {
  border: '1px solid #ccc',
  borderRadius: 4,
  padding: '6px 8px',
  width: 56,
  textAlign: 'center',
  fontSize: 15,
  fontWeight: 'bold',
  outline: 'none',
  marginTop: 2,
};

/* ─── Pick-ticket / load-sheet summary ─── */

export interface CedPickLine {
  label: string;
  qty: number;
}

export interface CedPickTicketProps {
  lines: CedPickLine[];
  total: number;
  unitPrice: number;
  /** When true, overlays a warehouse-style “PICKED” stamp (Place Order beat). */
  stamped?: boolean;
}

const STAMP_RED = '#c53030';

/** Compact packing-list strip of selected sizes + order total. */
export const CedPickTicket: React.FC<CedPickTicketProps> = ({ lines, total, unitPrice, stamped }) => {
  const reduceMotion = useReducedMotion();

  if (!lines.length) {
    return (
      <div style={pickWrapEmpty}>
        <div style={pickHdr}>Load sheet</div>
        <div style={pickEmpty}>Select sizes to build your pick ticket</div>
      </div>
    );
  }

  const pieceCount = lines.reduce((n, l) => n + l.qty, 0);

  return (
    <div style={{ ...pickWrap, position: 'relative', overflow: 'visible', minHeight: stamped ? 120 : undefined }} aria-label="Order load sheet">
      <div style={pickHdrRow}>
        <span style={pickHdr}>Pick ticket</span>
        <span style={pickMeta}>{pieceCount} pc · ${unitPrice.toFixed(2)}/ea</span>
      </div>
      <div style={pickLines}>
        {lines.map((line) => (
          <div key={line.label} style={pickLine}>
            <span style={pickSize}>{line.label}</span>
            <span style={pickDots} aria-hidden />
            <span style={pickQty}>×{line.qty}</span>
          </div>
        ))}
      </div>
      <div style={pickTotalRow}>
        <span>Order total</span>
        <span style={pickTotal}>${total.toFixed(2)}</span>
      </div>

      <AnimatePresence>
        {stamped && (
          <motion.div
            key="picked-stamp"
            aria-hidden
            style={pickedStampWrap}
            initial={reduceMotion ? false : { opacity: 0, scale: 1.55, rotate: -28, x: '-50%', y: '-50%' }}
            animate={{ opacity: 0.92, scale: 1, rotate: -14, x: '-50%', y: '-50%' }}
            exit={{ opacity: 0 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { type: 'spring', stiffness: 420, damping: 22, mass: 0.7 }
            }
          >
            <div style={pickedStamp}>
              <FaBolt size={11} style={{ marginRight: 5, opacity: 0.9 }} />
              PICKED
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const pickWrap: React.CSSProperties = {
  width: '92%',
  maxWidth: 420,
  margin: '4px auto 12px',
  padding: '12px 14px',
  borderRadius: 8,
  border: `1px dashed ${NAVY}`,
  background: 'linear-gradient(180deg, #fff 0%, #f7fafc 100%)',
  fontFamily: 'CenturyGothic, Arial, sans-serif',
  boxSizing: 'border-box',
};

const pickWrapEmpty: React.CSSProperties = {
  ...pickWrap,
  borderStyle: 'dashed',
  borderColor: '#cbd5e0',
  opacity: 0.85,
};

const pickHdrRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: 8,
  gap: 8,
};

const pickHdr: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: NAVY,
};

const pickMeta: React.CSSProperties = {
  fontSize: 11,
  color: '#718096',
};

const pickEmpty: React.CSSProperties = {
  fontSize: 12,
  color: '#a0aec0',
  textAlign: 'center',
  padding: '4px 0 2px',
};

const pickLines: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const pickLine: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: '#2d3748',
};

const pickSize: React.CSSProperties = {
  flexShrink: 0,
  fontWeight: 600,
};

const pickDots: React.CSSProperties = {
  flex: 1,
  borderBottom: '1px dotted #cbd5e0',
  height: 0,
  minWidth: 12,
  marginBottom: 2,
};

const pickQty: React.CSSProperties = {
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
  color: NAVY,
  fontWeight: 700,
};

const pickTotalRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 10,
  paddingTop: 8,
  borderTop: `1px solid ${DIM}`,
  fontSize: 13,
  color: '#4a5568',
  fontWeight: 600,
};

const pickTotal: React.CSSProperties = {
  fontSize: 16,
  color: NAVY,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
};

const pickedStampWrap: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  pointerEvents: 'none',
  zIndex: 2,
};

const pickedStamp: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 12px',
  border: `2.5px solid ${STAMP_RED}`,
  borderRadius: 4,
  color: STAMP_RED,
  background: 'rgba(255, 250, 245, 0.55)',
  fontFamily: "Georgia, 'Times New Roman', serif",
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  boxShadow: `inset 0 0 0 1px rgba(197, 48, 48, 0.25), 0 1px 2px rgba(26, 54, 93, 0.08)`,
  mixBlendMode: 'multiply',
};

/* ─── Ambient: live-circuit edge on checkout card (ONE effect) ─── */

export interface CedCheckoutShellProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

/** Wraps the CED checkout card with a very low-intensity animated edge accent. */
export const CedCheckoutShell: React.FC<CedCheckoutShellProps> = ({ children, style, className }) => {
  const reduceMotion = useReducedMotion();

  return (
    <div style={{ position: 'relative', width: '100%' }} className={className}>
      <motion.div
        aria-hidden
        style={circuitRing}
        animate={
          reduceMotion
            ? { opacity: 0.35 }
            : { opacity: [0.22, 0.4, 0.22], backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }
        }
        transition={
          reduceMotion
            ? { duration: 0 }
            : { duration: 5.5, repeat: Infinity, ease: 'easeInOut' }
        }
      />
      <div style={{ ...style, position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
};

const circuitRing: React.CSSProperties = {
  position: 'absolute',
  inset: -1,
  borderRadius: 21,
  pointerEvents: 'none',
  zIndex: 0,
  backgroundImage: `linear-gradient(110deg, ${NAVY} 0%, rgba(237,137,54,0.45) 42%, ${SPARK}55 50%, rgba(237,137,54,0.45) 58%, ${NAVY} 100%)`,
  backgroundSize: '220% 100%',
  opacity: 0.28,
  boxShadow: 'none',
};
